import React, { useState, useCallback } from 'react';
import AppPagePanel from 'components/ui/AppPagePanel';
import Modal from 'components/ui/Modal';
import { useFeatureAccess, handleAiLimitReached } from '../../pricing';
import { generateInsightsSummary, suggestOpportunities, deleteInsightsSummary } from 'api/ai';
import { useStore } from 'store/useStore';
import { InsightNode, InsightsSummary, SuggestedOpportunity } from 'types';
import SummaryHistoryCard from './SummaryHistoryCard';

interface Props {
    projectId: string;
    insights: InsightNode[];
    summaries: InsightsSummary[];
    onSummaryGenerated: (summary: InsightsSummary) => void;
    onSummaryDeleted: (generatedAt: string) => void;
    onOpportunitiesSuggested: (opportunities: SuggestedOpportunity[]) => void;
    onOpportunityGeneratingChange: (generating: boolean) => void;
    existingUsers: string[];
    onUserClick: (name: string) => void;
    /**
     * User-supplied instructions tailoring the summary/opportunity generation. Edited and
     * persisted in the separate CustomInstructionsPanel — this panel just forwards the
     * current saved value along with each generate/regenerate request.
     */
    customInstructions: string;
}

function renderInline(text: string): React.ReactNode {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={i}>{part.slice(2, -2)}</strong>
            : part,
    );
}

function renderSummaryContent(
    text: string,
    userNames: string[],
    onUserClick: (name: string) => void,
): React.ReactNode {
    const lines = text.split('\n');
    const nodes: React.ReactNode[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.startsWith('## ') || line.startsWith('### ')) {
            const content = line.startsWith('### ') ? line.slice(4) : line.slice(3);
            const trimmed = content.trim();
            const isUserHeading = userNames.includes(trimmed);
            nodes.push(
                <h3 key={i} className='insights-summary__heading'>
                    {isUserHeading ? (
                        <button
                            className='insights-summary__user-pill'
                            onClick={() => onUserClick(trimmed)}
                            title={`Filter by ${trimmed}`}
                        >
                            {trimmed}
                        </button>
                    ) : (
                        renderInline(content)
                    )}
                </h3>,
            );
        } else if (line.trim() === '') {
            nodes.push(<div key={i} className='insights-summary__spacer' />);
        } else {
            nodes.push(
                <p key={i} className='insights-summary__para'>
                    {renderInline(line)}
                </p>,
            );
        }
    }

    return <>{nodes}</>;
}

function formatSummaryDate(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export default function InsightsSummaryPanel({ projectId, insights, summaries, onSummaryGenerated, onSummaryDeleted, onOpportunitiesSuggested, onOpportunityGeneratingChange, existingUsers, onUserClick, customInstructions }: Props) {
    const { access, tryAccess } = useFeatureAccess('insights_ai_summary');
    const aiModelQuality = useStore((s) => s.aiModelQuality);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSuggestingOpps, setIsSuggestingOpps] = useState(false);
    const [streamedText, setStreamedText] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [oppNotice, setOppNotice] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedSummary, setSelectedSummary] = useState<InsightsSummary | null>(null);

    const showStreamedContent = streamedText.length > 0;

    /**
     * Regenerates BOTH the insights summary (streamed) and the opportunity suggestions in
     * parallel, using the current custom instructions. `force` is passed through to the
     * opportunity branch: false reconsiders only not-yet-consumed insights; true reconsiders
     * every insight and resets the consumption tracking (the "Regenerate All" escape hatch).
     * The summary is always regenerated from all insights regardless of `force`.
     */
    const runGeneration = async (force: boolean) => {
        setIsGenerating(true);
        setStreamedText('');
        setError(null);
        setOppNotice(null);

        // Run summary streaming and opportunity suggestion in parallel.
        const [summaryResult, oppResult] = await Promise.all([
            generateInsightsSummary(projectId, insights, (chunk) => {
                setStreamedText((prev) => prev + chunk);
            }, customInstructions, aiModelQuality),
            (async () => {
                setIsSuggestingOpps(true);
                onOpportunityGeneratingChange(true);
                const r = await suggestOpportunities(projectId, insights, { force, customInstructions, modelQuality: aiModelQuality });
                setIsSuggestingOpps(false);
                onOpportunityGeneratingChange(false);
                return r;
            })(),
        ]);

        setIsGenerating(false);
        useStore.getState().refreshAiUsage();

        if (summaryResult.error) {
            // insights-summary carries the credit charge for this paired action (see
            // aiUsageLimiter.js) — this is the branch ai_limit_reached actually arrives on.
            if (!handleAiLimitReached(summaryResult)) setError(summaryResult.error);
        } else if (summaryResult.summary) {
            onSummaryGenerated(summaryResult.summary);
            setStreamedText('');
        }

        if (oppResult.error) {
            // Don't clobber a summary error already surfaced above. opportunity-suggestions
            // is tier-gated (403), not credit-metered, so handleAiLimitReached is a no-op here.
            if (!handleAiLimitReached(oppResult)) setError((prev) => prev ?? oppResult.error!);
        } else if (oppResult.opportunities && oppResult.opportunities.length > 0) {
            onOpportunitiesSuggested(oppResult.opportunities);
        } else if (oppResult.noNewInsights) {
            // Every insight has already been turned into committed opportunities, so there
            // is nothing new to suggest. Any existing pending suggestions stay as they are.
            setOppNotice('No new insights since the last opportunity suggestions were added to a tree. Add new insights to generate more.');
        }
    };

    const handleGenerate = async () => {
        if (!tryAccess()) return;
        if (insights.length === 0 || isGenerating || isSuggestingOpps) return;
        await runGeneration(false);
    };

    // Escape hatch: regenerate the summary AND opportunity suggestions from ALL insights,
    // ignoring which insights were already turned into committed tree opportunities, and
    // reset that consumption tracking.
    const handleRegenerateAll = async () => {
        if (!tryAccess()) return;
        if (insights.length === 0 || isGenerating || isSuggestingOpps) return;

        const confirmed = window.confirm(
            'Regenerate the summary and opportunity suggestions from ALL insights? This reconsiders every '
            + 'insight — including ones already turned into tree opportunities — and resets the tracking of '
            + 'which insights have been used.',
        );
        if (!confirmed) return;

        await runGeneration(true);
    };

    const handleCopy = useCallback(() => {
        const text = showStreamedContent ? streamedText : (selectedSummary?.content ?? '');
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [showStreamedContent, streamedText, selectedSummary]);

    const handleUserClickFromModal = (name: string) => {
        setSelectedSummary(null);
        onUserClick(name);
    };

    const handleDeleteSummary = useCallback(async () => {
        if (!selectedSummary) return;
        const confirmed = window.confirm('Permanently delete this summary? This cannot be undone.');
        if (!confirmed) return;

        setIsDeleting(true);
        const result = await deleteInsightsSummary(projectId, selectedSummary.generatedAt);
        setIsDeleting(false);

        if (result.error) {
            setError(result.error);
            return;
        }

        onSummaryDeleted(selectedSummary.generatedAt);
        setSelectedSummary(null);
    }, [selectedSummary, projectId, onSummaryDeleted]);

    const isLocked = access === 'locked';

    // Newest first: latest summary at the top.
    const displaySummaries = summaries;

    return (
        <AppPagePanel
            title='Insights Summary'
            style={{ minWidth: 0, flex: '0 0 auto', height: '37.5%' }}
            className='insights-summary-panel'
        >
            <div className='insights-summary-panel__controls'>
                <button
                    className={`insights-summary-panel__btn${isLocked ? ' insights-summary-panel__btn--locked' : ''}`}
                    onClick={handleGenerate}
                    disabled={isGenerating || isSuggestingOpps || insights.length === 0}
                    title={
                        insights.length === 0
                            ? 'Add insights first'
                            : isLocked
                                ? 'Available on Pro and Teams plans'
                                : undefined
                    }
                >
                    {isGenerating ? 'Generating…' : isSuggestingOpps ? 'Identifying…' : 'Update Summary'}
                </button>
                {isLocked && (
                    <span className='tier-badge'>Pro / Teams</span>
                )}
                {insights.length > 0 && (
                    <button
                        className='insights-summary-panel__regen-btn'
                        onClick={handleRegenerateAll}
                        disabled={isGenerating || isSuggestingOpps}
                        title='Regenerate the summary and reconsider every insight (including ones already used), resetting usage tracking'
                    >
                        ↻ Regenerate All
                    </button>
                )}
            </div>

            {error && (
                <p className='insights-summary-panel__error'>{error}</p>
            )}

            {!error && oppNotice && (
                <p className='insights-summary-panel__notice'>{oppNotice}</p>
            )}

            <div className='insights-summary-panel__result'>
                {showStreamedContent && (
                    <div className='insights-summary-panel__output'>
                        {!isGenerating && (
                            <div className='insights-summary-panel__output-header'>
                                <button
                                    className='insights-summary-panel__copy-btn'
                                    onClick={handleCopy}
                                    title='Copy to clipboard'
                                >
                                    {copied ? '✓ Copied' : 'Copy'}
                                </button>
                            </div>
                        )}
                        <div className='insights-summary-panel__content'>
                            {renderSummaryContent(streamedText, existingUsers, onUserClick)}
                            {isGenerating && <span className='insights-summary-panel__cursor' aria-hidden='true'>▋</span>}
                        </div>
                    </div>
                )}

                {!showStreamedContent && displaySummaries.length > 0 && (
                    <div className='insights-summary-panel__history-list'>
                        {displaySummaries.map((s) => (
                            <SummaryHistoryCard
                                key={s.generatedAt}
                                summary={s}
                                isLatest={s === summaries[0]}
                                onClick={() => setSelectedSummary(s)}
                            />
                        ))}
                    </div>
                )}

                {!showStreamedContent && summaries.length === 0 && !error && (
                    <div className='insights-summary-panel__empty-box'>
                        <p className='insights-summary-panel__empty'>
                            {insights.length === 0
                                ? <>Add insights to this project first,<br />then generate a summary.</>
                                : <>Click the button above to generate an<br />AI-powered analysis of your insights.</>}
                        </p>
                    </div>
                )}
            </div>

            {selectedSummary && (
                <Modal
                    title={`Summary — ${formatSummaryDate(selectedSummary.generatedAt)}`}
                    onClose={() => setSelectedSummary(null)}
                    closeOnOverlayClick
                    className='summary-view-modal'
                >
                    <div className='insights-summary-panel__output-header'>
                        <p className='insights-summary-panel__meta'>
                            Generated {formatSummaryDate(selectedSummary.generatedAt)}
                        </p>
                        <div className='insights-summary-panel__output-actions'>
                            <button
                                className='insights-summary-panel__copy-btn'
                                onClick={handleCopy}
                                disabled={isDeleting}
                                title='Copy to clipboard'
                            >
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                            <button
                                className='insights-summary-panel__delete-btn'
                                onClick={handleDeleteSummary}
                                disabled={isDeleting}
                                title='Permanently delete this summary'
                            >
                                {isDeleting ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    </div>
                    <div className='insights-summary-panel__content'>
                        {renderSummaryContent(selectedSummary.content, existingUsers, handleUserClickFromModal)}
                    </div>
                </Modal>
            )}
        </AppPagePanel>
    );
}
